import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFolder,
  deleteFile,
  deleteFolder,
  finalizeUploadedFile,
  getAllFolders,
  getFolderById,
  getFolders,
  getFiles,
  moveFile,
  moveFolder,
  renameFolder,
  requestFileUploadUrl,
  uploadFileToSignedUrl,
} from '../api/files-api';
import type { FileFilters } from '../types';

export const filesKeys = {
  all: ['project-files'] as const,
  folders: (projectId: number, parentId: string | null, filters: FileFilters) =>
    [...filesKeys.all, 'folders', projectId, parentId, filters] as const,
  allFolders: (projectId: number) => [...filesKeys.all, 'all-folders', projectId] as const,
  folder: (id: string) => [...filesKeys.all, 'folder', id] as const,
  files: (projectId: number, folderId: string | null, filters: FileFilters) =>
    [...filesKeys.all, 'files', projectId, folderId, filters] as const,
  projectScope: (projectId: number) => [...filesKeys.all, 'project', projectId] as const,
};

export const useFolders = (
  projectId: number | undefined,
  parentId: string | null,
  filters: FileFilters = {},
) =>
  useQuery({
    queryKey: filesKeys.folders(projectId!, parentId, filters),
    queryFn: () => getFolders(projectId!, parentId, filters),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useAllFolders = (projectId: number | undefined) =>
  useQuery({
    queryKey: filesKeys.allFolders(projectId!),
    queryFn: () => getAllFolders(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const useFolder = (id: string | undefined) =>
  useQuery({
    queryKey: filesKeys.folder(id!),
    queryFn: () => getFolderById(id!),
    enabled: !!id,
    staleTime: 60_000,
  });

export const useFiles = (
  projectId: number | undefined,
  folderId: string | null,
  filters: FileFilters = {},
) =>
  useQuery({
    queryKey: filesKeys.files(projectId!, folderId, filters),
    queryFn: () => getFiles(projectId!, folderId, filters),
    enabled: !!projectId,
    staleTime: 30_000,
  });

const useInvalidateProject = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return () => {
    if (!projectId) return;
    queryClient.invalidateQueries({ queryKey: [...filesKeys.all] });
  };
};

export const useCreateFolder = (projectId: number | undefined) => {
  const invalidate = useInvalidateProject(projectId);

  return useMutation({
    mutationFn: async ({
      parentId,
      name,
    }: {
      parentId: string | null;
      name: string;
    }) => {
      if (!projectId) throw new Error('A project id is required.');
      return createFolder({ project: projectId, parent: parentId, name });
    },
    onSuccess: () => invalidate(),
  });
};

export const useRenameFolder = (projectId: number | undefined) => {
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameFolder(id, name),
    onSuccess: () => invalidate(),
  });
};

export const useDeleteFolder = (projectId: number | undefined) => {
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: (id: string) => deleteFolder(id),
    onSuccess: () => invalidate(),
  });
};

export const useDeleteFile = (projectId: number | undefined) => {
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: (uuid: string) => deleteFile(uuid),
    onSuccess: () => invalidate(),
  });
};

export const useMoveFile = (projectId: number | undefined) => {
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: ({ uuid, folder }: { uuid: string; folder: string | null }) =>
      moveFile(uuid, folder),
    onSuccess: () => invalidate(),
  });
};

export const useMoveFolder = (projectId: number | undefined) => {
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: ({ id, parent }: { id: string; parent: string | null }) =>
      moveFolder(id, parent),
    onSuccess: () => invalidate(),
  });
};

export const useBulkDelete = (projectId: number | undefined) => {
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: async ({
      folderIds,
      fileUuids,
    }: {
      folderIds: string[];
      fileUuids: string[];
    }) => {
      await Promise.all([
        ...folderIds.map((id) => deleteFolder(id)),
        ...fileUuids.map((uuid) => deleteFile(uuid)),
      ]);
    },
    onSuccess: () => invalidate(),
  });
};

export const useBulkMove = (projectId: number | undefined) => {
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: async ({
      folderIds,
      fileUuids,
      destination,
    }: {
      folderIds: string[];
      fileUuids: string[];
      destination: string | null;
    }) => {
      await Promise.all([
        ...folderIds.map((id) => moveFolder(id, destination)),
        ...fileUuids.map((uuid) => moveFile(uuid, destination)),
      ]);
    },
    onSuccess: () => invalidate(),
  });
};

export const useUploadFiles = (
  projectId: number | undefined,
  folderId: string | null,
) => {
  const invalidate = useInvalidateProject(projectId);

  return useMutation({
    mutationFn: async ({
      files,
      onFileProgress,
    }: {
      files: File[];
      onFileProgress?: (index: number, progressPct: number) => void;
    }) => {
      if (!projectId) {
        throw new Error('A project id is required before uploading.');
      }

      const uploaded = [];
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const signed = await requestFileUploadUrl({
          project_id: projectId,
          folder_id: folderId,
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
        });

        await uploadFileToSignedUrl(file, signed.signed_upload_url, (pct) =>
          onFileProgress?.(i, pct),
        );

        const projectFile = await finalizeUploadedFile({
          project_id: projectId,
          folder_id: folderId,
          file_uuid: signed.file_uuid,
          gcs_object_path: signed.gcs_object_path,
          original_file_name: file.name,
          mime_type: file.type || '',
          file_size: file.size,
        });
        uploaded.push(projectFile);
      }
      return uploaded;
    },
    onSuccess: () => invalidate(),
  });
};
